import { WorkflowRuntimeListItem } from 'src/app/services/workflow/workflow.service'

/** The closed status vocabulary every workflow type maps onto. */
export type StatusToken = 'draft' | 'progress' | 'awaiting' | 'returned' | 'completed' | 'rejected'

export type StageSegment = 'done' | 'current' | 'todo'

/** Terminal items have no live SLA, so an overdue flag on one is stale data. */
export const OVERDUE_SUPPRESSING_STATUSES = ['COMPLETED', 'REJECTED']

/** Beyond this, segments are too thin to read — fall back to one continuous bar. */
export const MAX_READABLE_SEGMENTS = 8

function normalizedStatus(item: WorkflowRuntimeListItem): string {
	return (item.status || '').toString().trim().toUpperCase()
}

function isReturned(item: WorkflowRuntimeListItem): boolean {
	return (item.lastAction || '').toString().trim().toUpperCase() === 'RETURNED'
}

/** The backend stores UTC; a timezone-less timestamp must be parsed as UTC, not local. */
function asUtcDate(value: string): Date {
	const normalized = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : value + 'Z'
	return new Date(normalized)
}

export function statusToken(item: WorkflowRuntimeListItem): StatusToken {
	const status = normalizedStatus(item)
	if (status === 'COMPLETED') return 'completed'
	if (status === 'REJECTED') return 'rejected'
	if (status === 'DRAFT') return 'draft'
	if (isReturned(item) || item.canResubmit) return 'returned'
	if (item.canApprove) return 'awaiting'
	if (item.currentActorRole && item.currentActorRole !== 'INITIATOR') return 'awaiting'
	return 'progress'
}

export function statusText(item: WorkflowRuntimeListItem): string {
	const status = normalizedStatus(item)
	if (status === 'COMPLETED') return 'Completed'
	if (status === 'REJECTED') return 'Rejected'
	if (status === 'DRAFT') return 'Draft'
	if (isReturned(item) || item.canResubmit) return 'Returned'
	if (item.canApprove) return 'Needs approval'
	if (item.currentActorRole && item.currentActorRole !== 'INITIATOR') return 'Awaiting approval'
	return item.derivedStatusLabel || item.statusLabel || item.status
}

/**
 * Overdue is a secondary condition on the current stage, not a status — it renders as
 * inline text beside the stage label rather than as a competing badge.
 */
export function overdueLabel(item: WorkflowRuntimeListItem, now: number = Date.now()): string {
	if (!item.isOverdue) return ''
	if (OVERDUE_SUPPRESSING_STATUSES.includes(normalizedStatus(item))) return ''

	const deadline = item.slaDeadline ? asUtcDate(item.slaDeadline).getTime() : NaN
	if (isNaN(deadline)) return 'Overdue'

	const mins = Math.floor((now - deadline) / 60000)
	if (mins < 1) return 'Overdue'
	if (mins < 60) return `${mins}m overdue`

	const hrs = Math.floor(mins / 60)
	if (hrs < 24) return `${hrs}h overdue`

	return `${Math.floor(hrs / 24)}d overdue`
}

export function stageSegments(item: WorkflowRuntimeListItem): StageSegment[] {
	const total = item.stageCount || 0
	if (total <= 0) return []

	const status = normalizedStatus(item)
	if (status === 'DRAFT') return new Array(Math.min(total, MAX_READABLE_SEGMENTS)).fill('todo')
	if (status === 'COMPLETED') return new Array(Math.min(total, MAX_READABLE_SEGMENTS)).fill('done')
	if (total > MAX_READABLE_SEGMENTS) return ['current']

	const current = Math.min(Math.max(item.currentStageNumber || 1, 1), total)
	return Array.from({ length: total }, (_, i) =>
		i + 1 < current ? 'done' : i + 1 === current ? 'current' : 'todo'
	)
}

export function stageProgressLabel(item: WorkflowRuntimeListItem): string {
	const total = item.stageCount || 0
	if (total <= 0) return ''

	const status = normalizedStatus(item)
	if (status === 'DRAFT') return `Not submitted · ${total} stage${total === 1 ? '' : 's'}`
	if (status === 'COMPLETED') return `All ${total} stages complete`

	const current = Math.min(Math.max(item.currentStageNumber || 1, 1), total)
	if (status === 'REJECTED') return `Rejected at stage ${current} of ${total}`
	return `Stage ${current} of ${total}`
}
